import { Component, inject } from '@angular/core';
import { CTChartConfigurationService } from '@cloud-twin-v2/cloud-twin-shared/chart/services/ct-chart-configuration.service';
import { ChartWidgetSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/widget-settings';
import { ChartConfiguration } from '@common-modules/shared/charts/model/chart/chart-configuration';
import { IntegrableFormParams } from '@common-modules/shared/forms/integrable-form';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import {
  ChartLineTypeEnum,
  ChartSerieTypeEnum,
} from '@common-modules/wlm-charts/core/models/chart-serie-type.enum';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { plainToClass } from 'class-transformer';
import { IFiltrableItemDto } from '../../../../../../src/app/common-modules/shared/model/filtrable-items/filtrable-item.dto';
import { BaseDashboardSettingsComponent } from '../core/base-dashboard-settings.component';

class DashboardChartSettingsModel {
  chartName: string;
  showLegend?: boolean;
  showPercent?: boolean;
  showLabels?: boolean;
  series: DashboardChartSettingsSerieModel[];
}

class DashboardChartSettingsSerieModel {
  name?: string;
  type?: string;
  lineType?: string;
  lineSize?: string | number;
  color?: string;
}

@Component({
  selector: 'ct-dashboard-chart-settings',
  templateUrl: './dashboard-chart-settings.component.html',
  styleUrl: './dashboard-chart-settings.component.scss',
  standalone: false,
})
export class DashboardChartSettingsComponent extends BaseDashboardSettingsComponent {
  initialModel: DashboardChartSettingsModel;
  chartSettings: ChartConfiguration;
  formOptions: FormlyFormOptions = {};
  items: IFiltrableItemDto[];
  activateModelChange = false;
  private _updatedSettings: ChartConfiguration;
  private _widgetSettings: ChartWidgetSettings;
  private readonly _chartConfigurationService = inject(CTChartConfigurationService);

  fieldConfig: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          key: 'chartName',
          type: 'input',
          className: 'col-8',
          props: {
            label: 'Title',
            required: true,
          },
        },
        {
          key: 'showLegend',
          type: 'checkbox',
          className: 'col-4 formly-field-no-border',
          defaultValue: true,
          props: {
            label: 'Show Legend',
          },
        },
      ],
    },
  ];

  private updateSeriesFields() {
    if (!this.initialModel?.series) return;
    this.initialModel.series.forEach((serie, idx) => {
      this.fieldConfig[0].fieldGroup.push({
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            key: `series[${idx}].name`,
            type: 'input',
            className: 'col-2',
            props: {
              label: 'Series Name',
              required: true,
            },
          },
          {
            key: `series[${idx}].type`,
            type: 'select',
            className: 'col-4',
            props: {
              label: 'Series Type',
              required: true,
              options: this.getSerieTypeOptions(),
            },
          },
          {
            key: `series[${idx}].lineType`,
            type: 'select',
            defaultValue: 'solid',
            className: 'col-2',
            props: {
              label: 'Series Line',
              required: true,
              options: this.getLineTypeOptions(),
            },
            hideExpression: `!model.series[${idx}] || model.series[${idx}].type !== 'line'`,
          },
          {
            key: `series[${idx}].lineSize`,
            type: 'input',
            className: undefined,
            props: {
              label: 'Line Thickness',
              type: 'number',
              required: false,
              min: 1,
              max: 10,
              step: 1,
              placeholder: 'Enter size',
            },
            expressionProperties: {
              className: (model) => {
                return !model.series || !model.series[idx] || model.series[idx].type !== 'line'
                  ? 'col-4'
                  : 'col-2';
              },
            },
          },
          {
            key: `series[${idx}].color`,
            type: 'color-picker-no-wrapper',
            className: 'col-2',
            props: {
              label: 'Series Color',
              customizableWidthAndHeight: 23,
            },
          },
        ],
      });
    });
  }

  setExtraParams(params: IFiltrableItemDto[]): void {
    this.items = params;
    this.onItemsChange(this.items);
  }

  setInitialModel(model: ChartWidgetSettings): void {
    this._widgetSettings = model;
    this.chartSettings = plainToClass(ChartConfiguration, model.chartConfiguration);
    this.initialModel = this.settingsToModel(this.chartSettings);
    if (!this._updatedSettings) {
      this.onModelChange(this.initialModel);
    }
    this.updateSeriesFields();
  }

  onModelChange(model: DashboardChartSettingsModel): void {
    this._updatedSettings = this.modelToSettings(model);
    this._widgetSettings.chartConfiguration = this._updatedSettings;
  }

  onIsValid(isValid: boolean): void {
    this.setIsValid(isValid);
  }

  onHasChanges(hasChanges: boolean): void {
    this.setHasChanges(hasChanges);
  }

  getWidgetSettings(): ChartWidgetSettings {
    return this._widgetSettings;
  }

  getModel() {
    return this._updatedSettings;
  }

  setParams(params: IntegrableFormParams): void {
    this.formOptions.formState.op = params.op;
    this.formOptions = globalUtilsHelper.clone(this.formOptions);
  }

  private settingsToModel(settings: ChartConfiguration): DashboardChartSettingsModel {
    if (!settings) {
      return null;
    }

    const model = {
      chartName: settings.customization?.name || '',
      showLegend: settings.customization?.showLegend || false,
      showLabels: settings.customization?.showLabels || false,
      series: (settings.seriesConfiguration || []).map((serie) => ({
        name: serie.serieCustomization?.name || '',
        type: serie.serieCustomization?.type || '',
        lineType: serie.serieCustomization?.lineType || 'solid',
        lineSize:
          serie.serieCustomization?.lineSize !== undefined &&
          serie.serieCustomization?.lineSize !== ''
            ? Number(serie.serieCustomization?.lineSize)
            : 2,
        color: serie.serieCustomization?.hexColor || '',
      })),
    };

    return model;
  }

  onItemsChange(visibleItems: IFiltrableItemDto[]): void {
    if (!visibleItems || visibleItems.length === 0) {
      this.chartSettings = undefined;
      this.initialModel.series = [];
      this.onIsValid(false);
      if (this.fieldConfig && this.fieldConfig[0] && this.fieldConfig[0].fieldGroup) {
        this.fieldConfig[0].fieldGroup = this.fieldConfig[0].fieldGroup.slice(0, 2);
      }
      return;
    }

    const currentModel = this.getCurrentFormModel();

    if (this.chartSettings && this.chartSettings.seriesConfiguration) {
      const visibleKeys = new Set((visibleItems || []).map((item) => item.filtrableType?.key));
      const seriesConfig = this.chartSettings.seriesConfiguration;

      const seriesKeys = seriesConfig.map((serie) => serie.serieDefinition?.filtrableType?.key);
      currentModel.series = currentModel.series.filter((serie, idx) => {
        const key = seriesKeys[idx];
        return key && visibleKeys.has(key);
      });
    }

    this._chartConfigurationService
      .getChartConfigurationFromFiltrableItems(visibleItems)
      .subscribe({
        next: (newConfig: ChartConfiguration) => {
          const mergedConfig = this.mergeUserChangesWithNewConfig(newConfig, currentModel);
          this.chartSettings = mergedConfig;
          this.initialModel = this.settingsToModel(this.chartSettings);
          this.fieldConfig[0].fieldGroup = this.fieldConfig[0].fieldGroup.slice(0, 2);
          this.updateSeriesFields();
          this.onModelChange(this.initialModel);
          this.activateModelChange = true;
          this.onIsValid(true);
        },
        error: (error) => {},
      });
  }

  private modelToSettings(model: DashboardChartSettingsModel): ChartConfiguration {
    if (!model || !this.chartSettings) {
      return null;
    }

    this.chartSettings.customization.name = model.chartName || '';
    this.chartSettings.customization.showLegend = model.showLegend || false;
    this.chartSettings.customization.showLabels = model.showLabels || false;

    if (this.chartSettings.seriesConfiguration && model.series) {
      model.series.forEach((serie, idx) => {
        const serieCustomization = this.chartSettings.seriesConfiguration[idx]?.serieCustomization;
        if (serieCustomization) {
          serieCustomization.name = serie.name || '';
          serieCustomization.type = (serie.type as ChartSerieTypeEnum) || serieCustomization.type;
          serieCustomization.lineType =
            (serie.lineType as ChartLineTypeEnum) || serieCustomization.lineType;
          serieCustomization.lineSize =
            typeof serie.lineSize === 'number' && !isNaN(serie.lineSize)
              ? String(serie.lineSize)
              : serieCustomization.lineSize;
          serieCustomization.hexColor = serie.color || serieCustomization.hexColor;
        }
      });
    }

    this.checkDatesAndFunctions(this.chartSettings);

    return this.chartSettings;
  }

  private getSerieTypeOptions() {
    return Object.entries(ChartSerieTypeEnum).map(([key, value]) => ({
      label: key,
      value: value,
    }));
  }

  private getLineTypeOptions() {
    return Object.entries(ChartLineTypeEnum).map(([key, value]) => ({
      label: this.renderLineTypeLabel(value),
      value: value,
    }));
  }

  private renderLineTypeLabel(type: string): string {
    switch (type) {
      case 'solid':
        return '───────────';
      case 'dotted':
        return '···········';
      default:
        return type;
    }
  }

  private getCurrentFormModel(): DashboardChartSettingsModel | null {
    if (this._updatedSettings) {
      return this.settingsToModel(this._updatedSettings);
    }

    return null;
  }

  private checkDatesAndFunctions(chartSettings: ChartConfiguration): void {
    if (chartSettings.seriesConfiguration) {
      chartSettings.seriesConfiguration.forEach((serie) => {
        if (
          serie.serieDefinition?.startDate &&
          typeof serie.serieDefinition.startDate === 'string'
        ) {
          serie.serieDefinition.startDate = new Date(serie.serieDefinition.startDate);
        }
        if (serie.serieDefinition?.endDate && typeof serie.serieDefinition.endDate === 'string') {
          serie.serieDefinition.endDate = new Date(serie.serieDefinition.endDate);
        }
      });
    }

    if (chartSettings.verticalAxis && Array.isArray(chartSettings.verticalAxis)) {
      chartSettings.verticalAxis.forEach((axis) => {
        if (axis && typeof axis.getName !== 'function') {
          axis.getName = function () {
            return '';
          };
        }
      });
    }
    if (chartSettings.horizontalAxis && Array.isArray(chartSettings.horizontalAxis)) {
      chartSettings.horizontalAxis.forEach((axis) => {
        if (axis && typeof axis.getName !== 'function') {
          axis.getName = function () {
            return '';
          };
        }
      });
    }
  }

  private mergeUserChangesWithNewConfig(
    newConfig: ChartConfiguration,
    currentModel: DashboardChartSettingsModel | null
  ): ChartConfiguration {
    if (!currentModel) return newConfig;

    if (newConfig.customization) {
      newConfig.customization.name = currentModel.chartName || newConfig.customization.name;
      newConfig.customization.showLegend =
        currentModel.showLegend !== undefined
          ? currentModel.showLegend
          : newConfig.customization.showLegend;
      newConfig.customization.showLabels =
        currentModel.showLabels !== undefined
          ? currentModel.showLabels
          : newConfig.customization.showLabels;
      if (currentModel.showPercent !== undefined) {
        (newConfig.customization as any).showPercent = currentModel.showPercent;
      }
    }

    if (currentModel.series && newConfig.seriesConfiguration) {
      const userSeriesChanges = new Map<string, DashboardChartSettingsSerieModel>();

      currentModel.series.forEach((userSerie, index) => {
        const originalSerie = this.chartSettings?.seriesConfiguration?.[index];
        if (originalSerie?.serieDefinition?.filtrableType?.key) {
          const key = originalSerie.serieDefinition.filtrableType.key;
          userSeriesChanges.set(key, userSerie);
        }
      });

      newConfig.seriesConfiguration.forEach((serie, index) => {
        const key = serie.serieDefinition?.filtrableType?.key;

        if (key && userSeriesChanges.has(key)) {
          const userChanges = userSeriesChanges.get(key);
          if (serie.serieCustomization && userChanges) {
            serie.serieCustomization.name = userChanges.name || serie.serieCustomization.name;
            serie.serieCustomization.type =
              (userChanges.type as ChartSerieTypeEnum) || serie.serieCustomization.type;
            serie.serieCustomization.lineType =
              (userChanges.lineType as ChartLineTypeEnum) || serie.serieCustomization.lineType;
            serie.serieCustomization.hexColor =
              userChanges.color || serie.serieCustomization.hexColor;

            if (userChanges.lineSize !== undefined && userChanges.lineSize !== '') {
              serie.serieCustomization.lineSize =
                typeof userChanges.lineSize === 'number'
                  ? String(userChanges.lineSize)
                  : String(userChanges.lineSize);
            }
          }
        }
      });
    }

    return newConfig;
  }
}
